import React, { useState } from 'react';
import { useOrderTypeContext } from '../../Contexts/OrderTypeContext';
import { useLoginContext } from '../../Contexts/LoginContext';
import ButtonPrimary from './ButtonPrimary';
import { TbCircleCheckFilled, TbCircleFilled, TbUser, TbMail, TbPhone, TbLock, TbCalendar, TbGenderTransgender, TbIdBadge, TbCircle } from 'react-icons/tb';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useTranslation, Trans } from 'react-i18next';
import { useLoginHook } from '../../Hooks/LoginHook';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import { IMaskInput } from 'react-imask';
import { isCpfValid } from '../../Utils/Helpers';
import { getProfileGenderList } from '../../Utils/Constants/Global';
import TextInput from './InputText';
import CardBoxDefault from '../Layout/CardBoxDefault';
import Confirm from '../Layout/Confirm';
import Modal from '../Layout/Modal';
import CustomerService from '../../Services/CustomerService';
import { TokenManager } from '../../Services/TokenManager';

const getValidationSchema = (t: any) =>
    Yup.object().shape({
        first_name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        last_name: Yup.string()
            .min(2, t("form.validation.invalid_min_characters", {quantity: 2}))
            .required(t('form.validation.required')),
        cpf: Yup.string()
            .length(11, t('form.validation.invalid_document_cpf'))
            .required(t('form.validation.required'))
            .test('is-valid-cpf', t('form.validation.invalid_document_cpf'), (value) => isCpfValid(value || '')),
        email: Yup.string()
            .email(t('form.validation.invalid_email'))
            .required(t('form.validation.required')),
        mobile_phone: Yup.string()
            .min(10, t('form.validation.invalid_mobile_phone'))
            .required(t('form.validation.required')),
        gender: Yup.number().required(t('form.validation.required')),
        birthdate: Yup.date().required(t('form.validation.required')),
        password: Yup.string()
            .min(6, t("form.validation.invalid_min_characters", {quantity: 6}))
            .required(t('form.validation.required')),
        password_confirmation: Yup.string()
            .oneOf([Yup.ref('password')], t('form.validation.passwords_must_match'))
            .required(t('form.validation.required')),
        terms_accepted: Yup.boolean()
            .oneOf([true], t('form.validation.terms_required'))
            .required(t('form.validation.required')),
    });

const FormRegister = () => {
    const { t } = useTranslation();
    const validationSchema = getValidationSchema(t);
    const { orderType } = useOrderTypeContext();
    const { setShowLoginBasic, refreshUser } = useLoginContext();
    const { setMainLoading, setMainLoadingIcon, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconError } = useLayoutContext();
    const { getLinkToHome } = useStoreHook();
    const { handleRedirect } = useLoginHook();
    const navigate = useNavigate();
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);

    const initialData = {
        first_name: '',
        last_name: '',
        cpf: '',
        email: '',
        mobile_phone: '',
        gender: '',
        birthdate: '',
        password: '',
        password_confirmation: '',
        terms_accepted: false,
    };

    const handleRegister = async (data: any) => {
        try {
            setMainLoading(true);
            setMainLoadingTitle(t('register.loading_title'));
            setMainLoadingMessage(t('layout.loading_main.default_message'));
            setMainLoadingIcon(true);

            const customerService = new CustomerService(orderType);
            const response = await customerService.register(data);

            if (response?.token) {
                TokenManager.setToken(response.token);
                refreshUser();
                setShowLoginBasic(false);
                setMainLoading(false);
                handleRedirect();
            }
        } catch (error: any) {
            setMainLoadingIcon(false);
            setMainLoadingIconError(true);
            setMainLoadingTitle(t('register.form.error.title'));

            let errorMessage = error.response?.data?.message || t('register.form.error.message');

            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const messages = Object.values(errors).flat().join('\n');
                if (messages) {
                    errorMessage = messages;
                }
            }

            setMainLoadingMessage(errorMessage);
            setMainLoadingCloseButton(true);
        }
    };

    const handleCancel = () => {
        setShowLoginBasic(true);
        navigate(getLinkToHome());
    };

    const isFieldRequired = (schema: any, fieldName: string) => {
        const field = schema.describe().fields[fieldName];
        return field?.tests.some((test: any) => test.name === 'required');
    };

    return (
        <>
        <Formik
            initialValues={initialData}
            validationSchema={validationSchema}
            onSubmit={(values) => handleRegister(values)}
        >
            {({ setFieldValue, values, errors, touched }) => (
                <Form className="flex flex-col gap-6 w-full mx-auto">
                    {/* Informações Pessoais */}
                    <CardBoxDefault 
                        icon={<div className="p-2 bg-gray-50 rounded-lg text-gray-500"><TbUser size={24} className='shrink-0' /></div>} 
                        title={t('register.section.personal')} 
                        className='p-6 space-y-4'
                        >

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="first_name" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.first_name.label')}
                                    {isFieldRequired(validationSchema, 'first_name') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <TextInput
                                    id="first_name"
                                    name="first_name"
                                    type="text"
                                    placeholder={t('register.form.first_name.placeholder')}
                                    className="w-full"
                                    value={values.first_name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('first_name', e.target.value)}
                                />
                                <ErrorMessage name="first_name" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="last_name" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.last_name.label')}
                                    {isFieldRequired(validationSchema, 'last_name') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <TextInput
                                    id="last_name"
                                    name="last_name"
                                    type="text"
                                    placeholder={t('register.form.last_name.placeholder')}
                                    className="w-full"
                                    value={values.last_name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('last_name', e.target.value)}
                                />
                                <ErrorMessage name="last_name" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="cpf" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.cpf.label')}
                                    {isFieldRequired(validationSchema, 'cpf') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbIdBadge size={20} />
                                    </div>
                                    <IMaskInput
                                        id="cpf"
                                        name="cpf"
                                        mask="000.000.000-00"
                                        unmask={true}
                                        placeholder="000.000.000-00"
                                        value={values.cpf}
                                        onAccept={(value) => setFieldValue('cpf', value)}
                                        className={`w-full rounded border-0 p-2.5 pl-10 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200`}
                                    />
                                </div>
                                <ErrorMessage name="cpf" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="birthdate" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.birthdate.label')}
                                    {isFieldRequired(validationSchema, 'birthdate') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbCalendar size={20} />
                                    </div>
                                    <TextInput
                                        id="birthdate"
                                        name="birthdate"
                                        type="date"
                                        className="w-full pl-10"
                                        value={values.birthdate}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('birthdate', e.target.value)}
                                    />
                                </div>
                                <ErrorMessage name="birthdate" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className='md:col-span-2'>
                                <label className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.gender.label')}
                                    <TbGenderTransgender size={18} className="text-gray-400" />
                                </label>
                                <div className="flex flex-wrap gap-2 pt-1">
                                    {getProfileGenderList().map((gender: any) => (
                                        <Button
                                            key={gender.type}
                                            type="button"
                                            onClick={() => setFieldValue('gender', gender.type)}
                                            className={`gap-1 ${values.gender === gender.type ? 'ring-custom-primary shadow-md' : 'bg-white text-gray-600 ring-gray-200 hover:ring-custom-primary hover:bg-gray-50'}`}
                                        >
                                            {values.gender === gender.type ? <TbCircleCheckFilled size={18} className='text-custom-primary -ms-1' /> : <TbCircle size={18} className="text-gray-200 -ms-1" />}
                                            {t(`profile.gender.${gender.type}`, gender.label)}
                                        </Button>
                                    ))}
                                </div>
                                <ErrorMessage name="gender" component="span" className="text-xs text-red-500" />
                            </div>
                        </div>
                    </CardBoxDefault>

                    {/* Contato */}
                    <CardBoxDefault 
                        icon={<div className="p-2 bg-sky-50 rounded-lg text-sky-500"><TbMail size={24} className='shrink-0' /></div>} 
                        title={t('register.section.contact')} 
                        className='p-6 space-y-4'
                        >

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="email" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.email.label')}
                                    {isFieldRequired(validationSchema, 'email') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbMail size={20} />
                                    </div>
                                    <TextInput
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="seu@email.com"
                                        className="w-full pl-10"
                                        value={values.email}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('email', e.target.value)}
                                    />
                                </div>
                                <ErrorMessage name="email" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="mobile_phone" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.mobile_phone.label')}
                                    {isFieldRequired(validationSchema, 'mobile_phone') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbPhone size={20} />
                                    </div>
                                    <IMaskInput
                                        id="mobile_phone"
                                        name="mobile_phone"
                                        mask="(00) 00000-0000"
                                        unmask={true}
                                        placeholder="(21) 99999-9999"
                                        value={values.mobile_phone}
                                        onAccept={(value) => setFieldValue('mobile_phone', value)}
                                        className={`w-full rounded border-0 p-2.5 pl-10 ring-1 ring-gray-300 placeholder:text-gray-300 font-bold focus:ring-2 focus:ring-gray-500 transition-all ease-in-out duration-200`}
                                    />
                                </div>
                                <ErrorMessage name="mobile_phone" component="span" className="text-xs text-red-500" />
                            </div>
                        </div>
                    </CardBoxDefault>

                    {/* Segurança */}
                    <CardBoxDefault 
                        icon={<div className="p-2 bg-amber-50 rounded-lg text-amber-500"><TbLock size={24} className='shrink-0' /></div>} 
                        title={t('register.section.security')} 
                        className='p-6 space-y-4'
                        >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="password" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.password.label')}
                                    {isFieldRequired(validationSchema, 'password') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbLock size={20} />
                                    </div>
                                    <TextInput
                                        id="password"
                                        name="password"
                                        type="password"
                                        placeholder="••••••••"
                                        className="w-full pl-10"
                                        value={values.password}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('password', e.target.value)}
                                    />
                                </div>
                                <ErrorMessage name="password" component="span" className="text-xs text-red-500" />
                            </div>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="password_confirmation" className="font-semibold text-shopfood-title flex items-center gap-1">
                                    {t('register.form.password_confirmation.label')}
                                    {isFieldRequired(validationSchema, 'password_confirmation') && <sup className="text-shopfood-notify"><TbCircleFilled size={7} /></sup>}
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <TbLock size={20} />
                                    </div>
                                    <TextInput
                                        id="password_confirmation"
                                        name="password_confirmation"
                                        type="password"
                                        placeholder="••••••••"
                                        className="w-full pl-10"
                                        value={values.password_confirmation}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('password_confirmation', e.target.value)}
                                    />
                                </div>
                                <ErrorMessage name="password_confirmation" component="span" className="text-xs text-red-500" />
                            </div>
                        </div>
                    </CardBoxDefault>

                    {/* Termos e Ações */}
                    <div className="rounded-xl px-6 space-y-6">
                        <div className="flex items-center gap-1">

                            {values.terms_accepted
                                ? <TbCircleCheckFilled size={20} className='shrink-0 text-custom-primary cursor-pointer' onClick={() => setFieldValue('terms_accepted', !values.terms_accepted)} />
                                : <TbCircle size={20} className='shrink-0 text-gray-400 cursor-pointer' onClick={() => setFieldValue('terms_accepted', !values.terms_accepted)} />}

                            <div className="flex flex-col">
                                <label
                                    className="text-sm text-gray-600 cursor-pointer leading-4"
                                    onClick={() => setFieldValue('terms_accepted', !values.terms_accepted)}
                                >
                                    <Trans
                                        i18nKey="register.form.terms_accepted.label"
                                        components={{
                                            termsLink: <span className="text-custom-primary hover:underline font-bold" onClick={(e) => { e.stopPropagation(); navigate('/page/termos-de-uso'); }} />
                                        }}
                                    />
                                </label>
                                <ErrorMessage name="terms_accepted" component="span" className="text-xs text-red-500" />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <ButtonPrimary 
                                type="submit" 
                                className="flex-1 text-nowrap md:max-w-fit" 
                                onClick={() => {}}
                            >
                                <span>{t('register.form.button_submit.label')}</span>
                            </ButtonPrimary>

                            <Button 
                                onClick={() => setShowConfirmCancel(true)} 
                                className="flex-1 md:max-w-fit" 
                            >
                                {t('login.already_registered_title')}
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>

        <Modal open={showConfirmCancel} onClose={() => setShowConfirmCancel(false)} className="max-w-sm m-4">
            <Confirm 
                title={t('register.form.confirm.title')}
                message={t('register.form.confirm.message')}
                onConfirm={handleCancel}
                onCancel={() => setShowConfirmCancel(false)}
            />
        </Modal>
        </>
    );
};

export default FormRegister;

